import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const items = [
  {
    kind: "product",
    tag: "K5 Drinking Station",
    name: "K5 Drinking Water Station",
    desc: "Kinetico's best reverse-osmosis system. Configurable multi-stage filtration with VOC Guard — bottle-quality water right from your faucet.",
    img: "https://www.kinetico.com/media/bjjkxtkr/img-k5-drinking-water-station-24.png?width=1200&height=1200",
    frame: "product-frame",
    span: "lg:col-span-7",
  },
  {
    kind: "product",
    tag: "Q-Series Softener",
    name: "Kinetico Q950 Premier",
    desc: "Non-electric twin-tank softener engineered for the way real families use water — endless soft water, on-demand regeneration, 10-year warranty.",
    img: "https://www.kinetico.com/media/whbcz3iz/q850-desaturated-sump.png?width=1200&height=1200",
    frame: "product-frame-alt",
    span: "lg:col-span-5",
  },
  {
    kind: "lifestyle",
    tag: "Happy Homes",
    name: "Water your family can taste.",
    desc: "Softer showers, brighter laundry, better coffee. It's the quiet difference our customers describe first — and can't imagine living without.",
    img: "https://images.unsplash.com/photo-1577896850715-ed0b7e3ece57?crop=entropy&cs=srgb&fm=jpg&w=1000&q=72&auto=format",
    frame: "product-frame",
    span: "lg:col-span-5",
  },
  {
    kind: "product",
    tag: "HYDRO ECO",
    name: "Kinetico HYDRO® ECO™",
    desc: "The next-generation tankless RO. 75% smaller footprint, smart LED display, built-in leak detection — Kinetico's most modern drinking-water system.",
    img: "https://www.kinetico.com/media/222ih3om/hydro-eco.png?width=1200&height=1200",
    frame: "product-frame-alt",
    span: "lg:col-span-7",
  },
  {
    kind: "lifestyle",
    tag: "Certified Install",
    name: "Every tap, done right.",
    desc: "Every install is handled personally by Vanguard — clean, code-compliant, and typically finished in a single afternoon.",
    img: "https://images.unsplash.com/photo-1571843439991-dd2b8e051966?crop=entropy&cs=srgb&fm=jpg&w=1000&q=72&auto=format",
    frame: "product-frame",
    span: "lg:col-span-6 lg:col-start-4",
  },
];

function ProductCard({ p, i }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  const isLifestyle = p.kind === "lifestyle";

  return (
    <Reveal delay={i * 0.06} className={`col-span-12 ${p.span}`} testId={`product-card-${i}`}>
      <div className="group">
        <div
          ref={ref}
          className={`relative overflow-hidden ${p.frame} aspect-[4/5]`}
          style={{ background: isLifestyle ? "#071824" : "#F4F7FB" }}
        >
          {isLifestyle ? (
            <>
              {/* Full-bleed lifestyle photograph with parallax */}
              <motion.div
                style={{ y: imgY, willChange: "transform" }}
                className="absolute inset-0"
              >
                <img
                  src={p.img}
                  alt={p.name}
                  className="absolute inset-0 w-full h-[115%] object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                  loading="lazy"
                />
              </motion.div>
              {/* Gradient overlay for legibility */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(4,15,20,0.05) 0%, rgba(4,15,20,0.15) 45%, rgba(4,15,20,0.85) 100%)",
                }}
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />

              {/* Spotlight number */}
              <div className="absolute top-6 left-6 font-editorial italic text-white/85 text-4xl">
                0{i + 1}
              </div>

              <div className="absolute bottom-6 left-6 right-6">
                <div className="text-[10px] uppercase tracking-[0.35em] text-[color:var(--blue)] font-medium">
                  {p.tag}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Blue spotlight glow behind product */}
              <div
                aria-hidden
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[75%] h-[65%] rounded-full"
                style={{
                  background:
                    "radial-gradient(ellipse, rgba(59,143,245,0.22) 0%, transparent 70%)",
                  filter: "blur(30px)",
                }}
              />
              {/* Navy floor gradient */}
              <div
                aria-hidden
                className="absolute inset-x-0 bottom-0 h-[35%]"
                style={{
                  background:
                    "linear-gradient(180deg, transparent 0%, rgba(30,58,138,0.15) 100%)",
                }}
              />
              {/* Product image — hi-res, fills the frame */}
              <motion.div
                style={{ y: imgY, willChange: "transform" }}
                className="absolute inset-0 flex items-center justify-center p-6 lg:p-10"
              >
                <img
                  src={p.img}
                  alt={p.name}
                  style={{
                    filter: "drop-shadow(0 30px 50px rgba(10,30,60,0.25)) contrast(1.04)",
                  }}
                  className="w-full h-full object-contain transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
                  loading="lazy"
                />
              </motion.div>
              <div className="absolute inset-0 ring-1 ring-inset ring-black/10" />

              {/* Spotlight number */}
              <div className="absolute top-6 left-6 font-editorial italic text-[#0c2436]/60 text-4xl">
                0{i + 1}
              </div>

              <div className="absolute bottom-6 left-6 right-6">
                <div className="text-[10px] uppercase tracking-[0.35em] text-[#0a3a80] font-medium">
                  {p.tag}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 flex items-start justify-between gap-6">
          <div className="flex-1">
            <h3 className="font-display font-semibold text-[clamp(1.6rem,2.4vw,2.4rem)] text-white leading-[1.05]">
              {p.name}
            </h3>
            <p className="mt-3 text-[14.5px] leading-[1.7] text-[color:var(--muted)] font-body max-w-md">
              {p.desc}
            </p>
          </div>
          <Link
            to="/products"
            data-testid={`product-link-${i}`}
            className="shrink-0 mt-2 h-11 w-11 grid place-items-center rounded-full border border-white/15 text-white hover:border-[color:var(--blue)] hover:text-[color:var(--blue)] transition-colors"
            aria-label={`Learn more about ${p.name}`}
          >
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </Reveal>
  );
}

export default function ProductShowcase() {
  return (
    <section
      className="relative py-32 lg:py-40 px-6 lg:px-10 border-t border-[color:var(--border)]"
      data-testid="product-showcase"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 mb-20">
          <div className="col-span-12 lg:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.35em] text-[color:var(--blue)]">Systems</div>
            <div className="mt-2 text-[11px] uppercase tracking-[0.35em] text-[color:var(--muted)]">Built by Kinetico</div>
          </div>
          <div className="col-span-12 lg:col-span-9">
            <Reveal>
              <h2 className="font-display font-semibold text-[clamp(2.5rem,7vw,6rem)] leading-[0.95] text-white">
                Every drop,<br />
                <span className="font-editorial italic font-normal text-[color:var(--blue)]">
                  engineered.
                </span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-xl text-[15px] leading-[1.75] text-[color:var(--muted)] font-body">
                Kinetico builds the systems. We size, install and stand behind them. The result is water that just works — for the entire lifetime of your home.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 lg:gap-10 gap-y-24">
          {items.map((p, i) => (
            <ProductCard key={p.name} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
